import { Component, Host, Prop, h, Event, EventEmitter, Method, State, Element } from '@stencil/core';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/option/option.js';

/**
 * Componente select per filtri con supporto per opzioni e stato
 * @slot - Contenitore per elementi sl-option quando le opzioni sono definite come figli
 */
@Component({
  tag: 'jump-filter-select',
  styleUrl: 'jump-filter-select.scss',
  shadow: true,
})
export class JumpFilterSelect {
  /** Riferimento all'elemento host */
  @Element() hostElement: HTMLElement;

  /** Il valore selezionato */
  @Prop({ reflect: true, mutable: true }) value: string;

  /** Il placeholder da mostrare quando nessun valore è selezionato */
  @Prop() placeholder: string = 'Seleziona un\'opzione';

  /** L'etichetta del select */
  @Prop() label: string;

  /** Nome identificativo del filtro, utilizzato negli eventi */
  @Prop() name: string = 'select-filter';
  
  /** Opzioni del select come array di oggetti {value, label} o come stringa JSON */
  @Prop() options: string | { value: string; label: string }[] = [];

  /** Stato del select (disabilitato) */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** Flag per indicare se è possibile selezionare più opzioni */
  @Prop() multiple: boolean = false;

  /** Flag per indicare se la selezione è obbligatoria */
  @Prop() required: boolean = false;

  /** Flag per indicare se il select è in stato di caricamento */
  @Prop() loading: boolean = false;

  /** Stato interno per gestire l'apertura del dropdown */
  @State() open: boolean = false;
  
  /** Stato interno per tenere traccia dei valori selezionati come array per compatibilità con gli altri filtri */
  @State() values: string[] = [];

  /** Riferimento all'elemento host */
  host: HTMLElement;

  /** Riferimento all'elemento sl-select interno */
  el: any;

  /**
   * Converte le opzioni da string o array a array di oggetti
   */
  private getOptionsArray(): { value: string; label: string }[] {
    if (typeof this.options === 'string') {
      try {
        return JSON.parse(this.options);
      } catch (e) {
        console.error('Errore nel parsing delle opzioni:', e);
        return [];
      }
    }
    return this.options;
  }
  
  /**
   * Aggiorna l'array dei valori in base al valore selezionato
   */
  private updateValues() {
    this.values = this.value ? [this.value] : [];
  }

  componentDidLoad() {
    // Inizializza l'array dei valori
    this.updateValues();
    
    // Aspetta che il render sia completato e che Shoelace abbia processato gli slot
    setTimeout(() => {
      this.listenSLChange();
    }, 100); // Aumentiamo il timeout per dare più tempo a Shoelace
  }

  componentDidRender() {
    // Assicuriamoci che il listener sia attivo anche dopo ogni render
    if (this.el && !this.el._jumpListenerAttached) {
      this.listenSLChange();
    }
  }

  /**
   * Ascolta gli eventi di cambio selezione dal select Shoelace
   */
  listenSLChange() {
    if (this.el && !this.el._jumpListenerAttached) {
      // Marchiamo l'elemento per evitare listener multipli
      this.el._jumpListenerAttached = true;
      
      this.el.addEventListener('sl-change', (event: any) => {
        if (this.disabled) {
          return;
        }

        console.log('sl-change event received:', event.target.value);

        // AGGIORNA PRIMA I VALORI INTERNI
        this.value = event.target.value;
        this.updateValues();
  
        const optionsArray = this.getOptionsArray();
        let selectedOption = optionsArray.find(option => option.value === this.value);
        
        // Se non abbiamo trovato l'opzione nell'array (caso slot), proviamo a trovarla negli elementi sl-option
        if (!selectedOption && this.value) {
          const slotOptions = this.hostElement.querySelectorAll('sl-option');
          const slotOption = Array.from(slotOptions).find((opt: any) => opt.value === this.value);
          if (slotOption) {
            selectedOption = {
              value: this.value,
              label: slotOption.textContent || this.value
            };
          }
        }
  
        // Evento specifico per il componente select
        const selectEventData = {
          value: this.value,
          selectedOption
        };
        this.selectionChange.emit(selectEventData);
        
        // CORREZIONE: Evento standard per il sistema di filtri con valori aggiornati
        const filterEventData = {
          name: this.name,
          values: this.values, // Ora questo sarà popolato correttamente
          value: this.value,   // E anche questo
          selectedOption       // E anche questo
        };
        
        console.log('jump-filter-select: Emitting jump-filterchange with data:', filterEventData);
        this.selectionFilterChange.emit(filterEventData);
      });
    }
  }

  /**
   * Evento emesso quando cambia la selezione
   */
  @Event({ eventName: 'jump-select-change' }) selectionChange: EventEmitter;
  @Event({ eventName: 'jump-filterchange' }) selectionFilterChange: EventEmitter;
  /**
   * Evento emesso quando il valore viene cambiato programmaticamente
   */
  @Event({ eventName: 'jump-select-value-change' }) valueChange: EventEmitter;

  /**
   * Metodo pubblico per ottenere il valore selezionato
   * @returns Il valore attualmente selezionato
   */
  @Method()
  async getSelectedValue() {
    return this.value;
  }

  /**
   * Metodo pubblico per ottenere l'opzione selezionata
   * @returns L'opzione attualmente selezionata come oggetto {value, label}
   */
  @Method()
  async getSelectedOption() {
    const optionsArray = this.getOptionsArray();
    let selectedOption = optionsArray.find(option => option.value === this.value);
    
    // Se non abbiamo trovato l'opzione nell'array (caso slot), proviamo a trovarla negli elementi sl-option
    if (!selectedOption && this.value) {
      const slotOptions = this.hostElement.querySelectorAll('sl-option');
      const slotOption = Array.from(slotOptions).find((opt: any) => opt.value === this.value);
      if (slotOption) {
        selectedOption = {
          value: this.value,
          label: slotOption.textContent || this.value
        };
      }
    }
    
    return selectedOption;
  }
  
  /**
   * Metodo pubblico per ottenere i valori selezionati come array
   * @returns Array contenente il valore selezionato o un array vuoto
   */
  @Method()
  async getValues() {
    return this.values;
  }
  
  /**
   * Metodo pubblico per ottenere il nome del filtro
   * @returns Il nome del filtro
   */
  @Method()
  async getName() {
    return this.name;
  }

  /**
   * Metodo pubblico per impostare programmaticamente il valore selezionato
   * @param newValue - Il nuovo valore da selezionare
   * @param emitEvent - Se emettere l'evento di cambio (default: true)
   * @returns Il nuovo valore impostato
   */
  @Method()
  async setValue(newValue: string, emitEvent: boolean = true) {
    // Se il componente è disabilitato, non fare nulla
    if (this.disabled) {
      return this.value;
    }
    
    // Salva il valore precedente per l'evento
    const oldValue = this.value;
    
    // Non fare nulla se il valore non è cambiato
    if (oldValue === newValue) {
      return this.value;
    }
    
    this.value = newValue;
    this.updateValues();
  
    // Aggiorna anche il valore dell'elemento sl-select interno
    if (this.el) {
      this.el.value = newValue;
    }
  
    // Emetti l'evento di cambio se richiesto
    if (emitEvent) {
      const optionsArray = this.getOptionsArray();
      let selectedOption = optionsArray.find(option => option.value === this.value);
      
      // Se non abbiamo trovato l'opzione nell'array (caso slot), proviamo a trovarla negli elementi sl-option
      if (!selectedOption && this.value) {
        const slotOptions = this.hostElement.querySelectorAll('sl-option');
        const slotOption = Array.from(slotOptions).find((opt: any) => opt.value === this.value);
        if (slotOption) {
          selectedOption = {
            value: this.value,
            label: slotOption.textContent || this.value
          };
        }
      }
      
      // Evento specifico per il componente select
      const selectEventData = {
        value: this.value,
        oldValue: oldValue,
        selectedOption,
        programmatic: true // Flag per indicare che il cambio è avvenuto programmaticamente
      };
      this.selectionChange.emit(selectEventData);
      
      // Evento standard per il sistema di filtri
      const filterEventData = {
        name: this.name,
        values: this.values,
        value: this.value,
        oldValue: oldValue,
        selectedOption,
        programmatic: true
      };
      this.selectionFilterChange.emit(filterEventData);
      
      // Evento specifico per cambio programmato
      this.valueChange.emit({
        value: this.value,
        oldValue: oldValue,
        selectedOption
      });
    }

    return this.value;
  }

  render() {
    const optionsArray = this.getOptionsArray();
    const hasOptionsFromProp = optionsArray && optionsArray.length > 0;

    return (
      <Host ref={(host: HTMLElement) => (this.host = host)}>
        {this.label && (
          <div class="select-label">{this.label}</div>
        )}
        <sl-select
          value={this.value}
          placeholder={this.placeholder}
          disabled={this.disabled}
          multiple={this.multiple}
          required={this.required}
          loading={this.loading}
          ref={(el: any) => (this.el = el)}
        >
          {/* Renderizza le opzioni dalla prop options se disponibili, altrimenti usa lo slot */}
          {hasOptionsFromProp ? (
            optionsArray.map(option => (
              <sl-option value={option.value}>{option.label}</sl-option>
            ))
          ) : (
            <slot></slot>
          )}
        </sl-select>
      </Host>
    );
  }
}